import { differenceInDays } from 'date-fns';
import { type TestScore } from '@common/@types/test_score';

type UITestScoreData = TestScore & {
  rankChanged: string;
  x: number;
  y: number;
};

export const transformTestScoreData = (data: TestScore[] | undefined): UITestScoreData[] => {
  const processedData: UITestScoreData[] = [];

  if (!data?.length) {
    return processedData;
  }

  const lastDate = data[0]?.test_date;

  for (let i = 0; i < data?.length; i++) {
    const current = data[i];
    const previous = data[i - 1];

    const currentPercentile = Number(current.percentile) ?? 0;
    const previoudPercentile = previous ? Number(previous.percentile) : 0;
    const diff = currentPercentile - previoudPercentile;

    let rankChangedText = `${Math.abs(diff)}`;

    if (diff > 0) {
      rankChangedText = `+${rankChangedText}`;
    } else if (diff < 0) {
      rankChangedText = `-${rankChangedText}`;
    }

    const year = differenceInDays(new Date(current.test_date), new Date(lastDate)) / 365;

    processedData.push({
      ...current,
      rankChanged: i === 0 ? '0' : rankChangedText,
      x: year,
      y: currentPercentile,
    });
  }

  return processedData;
};

/**
 * This returns empty array if no data or passed in data if <= 2 data items or
 * first and last test score.
 */
export const getInitialTestScoreDataSet = (
  data: UITestScoreData[] | undefined
): UITestScoreData[] => {
  if (!data?.length) {
    return [];
  }

  if (data.length <= 2) {
    return data;
  }

  return [data[0], data[data.length - 1]];
};

export const getDataFilterByMAPTestType = (data: Record<string, TestScore[]> | undefined) => {
  const filteredData: Record<string, TestScore[]> = {};

  if (!data) {
    return filteredData;
  }

  for (const [category, items] of Object.entries(data)) {
    filteredData[category] = items.filter((item) => item.test_type === 'MAP');
  }

  return filteredData;
};

export const getMinMaxPercentile = (
  data: TestScore[] | undefined
): { highestRank: number; lowestRank: number } => {
  const ranks = data ? data.map((item) => Number(item.percentile)) : [];

  if (!ranks.length) {
    return {
      highestRank: 0,
      lowestRank: 0,
    };
  }

  const highestRank = Math.max(...ranks);
  const lowestRank = Math.min(...ranks);

  return {
    highestRank,
    lowestRank,
  };
};
